import isNetworkError from 'is-network-error'
import { type KyInstance, isHTTPError, isTimeoutError } from 'ky'
import type {
  PaymentPlatform,
  PaymentSystemIdByPlatform,
  ProductMetadata,
  PurchasePayload,
  Result,
  TransactionListing,
} from './types'
import {
  AfterPaymentError,
  NativeStoreError,
  type NativeStoreErrorType,
  PromotionalOfferSignatureError,
  StaleTransactionError,
  SubscriptionConflictError,
} from './errors'
import { FailureCauseError, failureCause, tagFailure } from './failure'

export {
  AfterPaymentError,
  NativeStoreError,
  PromotionalOfferSignatureError,
  StaleTransactionError,
  SubscriptionConflictError,
} from './errors'

export interface SubscribeOptions<Product extends ProductMetadata> {
  api: {
    service: KyInstance
    receipt: KyInstance
    infra: KyInstance
  }
  platform: PaymentPlatform
  userId: number
  product: Product
  nativePurchase: (
    payload: PurchasePayload,
  ) => Promise<
    Result<
      { transaction_id: string },
      | 'interrupted'
      | 'pending'
      | 'product not found'
      | 'invalid transaction'
      | 'unknown'
    >
  >
  closeTransaction: (payload: {
    transaction_id: string
  }) => Promise<void> | void
  /** The native listing of the store's unclosed transactions. */
  getAllUnclosedTransactions: () => Promise<TransactionListing>
  receiptSystemAccountId: string | null
  /**
   * The chosen subscription offer to apply — native ≥ 5.19.0 selects it by
   * `offer_id`. On iOS a promotional offer also needs a fresh promotional
   * offer signature, fetched here before the order is created (ADR 0036).
   * Ignored for a consumable.
   */
  offer?: SubscribeOffer
  shouldResolve?: (
    status: ServiceLevelStatus,
  ) => status is SubscriptionServiceLevelStatus
  /**
   * Where the post-purchase poll reads the service level from. When omitted,
   * the Service API is polled every 100 ms. When provided, it is polled
   * sequentially — the next attempt starts 1 s after the previous one returns —
   * and a failed attempt does not end the poll. Both are bounded by the 15 s
   * deadline.
   */
  fetchServiceLevelStatus?: FetchServiceLevelStatus
  /**
   * The active subscription expiry captured before purchase. When omitted, the
   * caller guarantees that the user is not already subscribed.
   */
  currentSubscriptionExpiresAt?: string | null
  /**
   * Debug option to simulate AfterPaymentError for testing restore flow.
   * When true, throws AfterPaymentError after native purchase succeeds.
   */
  debugSimulateAfterPaymentError?: boolean
  onStepError?: (
    error: unknown,
    context: {
      step:
        | 'sign_promotional_offer'
        | 'create_order'
        | 'native_purchase'
        | 'mark_processing'
        | 'post_transaction'
        | 'list_unclosed_transactions'
        | 'close_transaction'
        | 'poll_service_level'
        | 'recover_interrupted_purchase'
      purchaseId?: number
      transactionId?: string
    },
  ) => void
  logEvent?: (payload: {
    event_name: string
    event_params?: Record<string, string>
  }) => Promise<void>
}

/**
 * The subscription offer a purchase applies. paywall-core's own minimal shape:
 * native-services' `SubscriptionOffer` is structurally assignable to it, but
 * cannot be imported here (native-services depends on paywall-core).
 */
export interface SubscribeOffer {
  id: string
  type: 'introductory' | 'promotional'
}

export const PaymentSystemID = {
  PingXX: 7,
  GooglePlay: 8,
  AppStoreStoreKit1: 9,
  AppStoreStoreKit2: 11,
}

export const paymentSystemIdByPlatform: PaymentSystemIdByPlatform = {
  china_android: PaymentSystemID.PingXX,
  googleplay_android: PaymentSystemID.GooglePlay,
  ios: PaymentSystemID.AppStoreStoreKit2,
}

export async function subscribe<Product extends ProductMetadata>({
  api,
  platform,
  userId,
  product,
  nativePurchase,
  closeTransaction,
  getAllUnclosedTransactions,
  receiptSystemAccountId,
  offer,
  shouldResolve,
  fetchServiceLevelStatus,
  currentSubscriptionExpiresAt,
  debugSimulateAfterPaymentError,
  onStepError,
  logEvent,
}: SubscribeOptions<Product>): Promise<SubscriptionServiceLevelStatus | null> {
  const logRequestErrorDialog = (error: unknown) => {
    const code = classifyErrorCode(error)
    void logEvent?.({
      event_name: 'component_event',
      event_params: {
        type: 'dialog',
        app_action: 'show',
        component_name:
          code === 'network' || code === 'timeout'
            ? 'internet_issue'
            : `error_${code}`,
      },
    })
  }

  /**
   * Signs an iOS promotional offer; `undefined` when the purchase uses none.
   * Its nonce is single-use, so every attempt to buy fetches a fresh
   * signature (ADR 0036). Throws a tagged `PromotionalOfferSignatureError`.
   */
  const signPromotionalOffer = async (): Promise<string | undefined> => {
    if (
      platform !== 'ios' ||
      product.product_type !== 'subscription' ||
      offer?.type !== 'promotional'
    ) {
      return undefined
    }
    try {
      return await fetchPromotionalOfferSignature(api.infra, {
        userId,
        sku: product.sku,
        offerId: offer.id,
      })
    } catch (cause) {
      logRequestErrorDialog(cause)
      const error = tagFailure(
        new PromotionalOfferSignatureError(cause),
        'sign_promotional_offer',
      )
      onStepError?.(error, { step: 'sign_promotional_offer' })
      throw error
    }
  }

  // 0. Sign an iOS promotional offer. Without a signature the purchase stops
  // here, before anything is bought or recorded.
  let promotionalOfferSignature = await signPromotionalOffer()

  // 1. Create order
  let purchaseId: number
  try {
    const response = await api.service
      .post(`store/users/${userId}/purchase-right`, {
        json: {
          product_id: product.product_id,
          iap_sku: product.sku,
        },
      })
      .json<PostPurchaseResponse>()
    purchaseId = response.results[0].purchase.id
  } catch (error) {
    logRequestErrorDialog(error)
    tagFailure(error, 'create_order')
    onStepError?.(error, { step: 'create_order' })
    throw error
  }

  /**
   * Marks the purchase record cancelled (no payment was taken) or failed,
   * without waiting: the purchase's outcome does not depend on it.
   */
  const markPurchase = (outcome: 'cancelled' | 'failed') => {
    void api.service
      .post(`store/users/${userId}/purchase/${purchaseId}/mark-${outcome}`)
      .catch(() => {})
  }

  const postTransaction = async (transactionId: string) => {
    let receiptRequestUrl = `projects/6/users/${userId}`
    let data = {}
    if (platform === 'china_android') {
      receiptRequestUrl += '/pending_iap_receipts'
      data = { receipt_data: transactionId }
    } else {
      receiptRequestUrl += '/subscriptions'
      data = { transaction_id: transactionId }
    }
    await api.receipt.post(receiptRequestUrl, {
      json: {
        ...data,
        sku_id: product.sku,
        payment_system_id: paymentSystemIdByPlatform[platform],
      },
    })
  }

  const postTransactionFailed = (error: unknown, transactionId: string) => {
    // Tag first: step telemetry renames the step's error.
    const afterPaymentError = tagFailure(
      new AfterPaymentError(
        purchaseId,
        transactionId,
        'post_transaction',
        error,
      ),
      'post_transaction',
      { purchaseId, transactionId },
    )
    onStepError?.(error, {
      step: 'post_transaction',
      purchaseId,
      transactionId,
    })
    return afterPaymentError
  }

  /**
   * The store still handed back a stale transaction after close all: no
   * payment was taken, so marks the purchase cancelled, not left awaiting
   * Restore Purchases (ADR 0045), and returns the error to throw. No third
   * purchase is made.
   */
  const cancelStalePurchase = (error: unknown, transactionId: string) => {
    markPurchase('cancelled')
    // Tag first: step telemetry renames the step's error, so it gets the
    // cause, never the error thrown.
    const staleTransactionError = tagFailure(
      new StaleTransactionError(purchaseId, transactionId, error),
      'post_transaction',
      { purchaseId },
    )
    onStepError?.(error, {
      step: 'post_transaction',
      purchaseId,
      transactionId,
    })
    return staleTransactionError
  }

  const executeNativePurchase = async (): Promise<
    string | SubscriptionServiceLevelStatus
  > => {
    const payload: PurchasePayload =
      product.product_type === 'subscription'
        ? {
            product_type: 'subscription',
            product_id: product.product_id,
            sku: product.sku,
            offer_id: offer?.id,
            ...(promotionalOfferSignature !== undefined && {
              offer_compact_jws: promotionalOfferSignature,
            }),
            receipt_system_account_id: receiptSystemAccountId,
          }
        : {
            product_type: 'consumable',
            product_id: product.product_id,
            sku: product.sku,
            receipt_system_account_id: receiptSystemAccountId,
          }
    let result: Awaited<ReturnType<typeof nativePurchase>>
    try {
      result = await nativePurchase(payload)
    } catch (error) {
      tagFailure(error, 'native_purchase', { purchaseId })
      onStepError?.(error, { step: 'native_purchase', purchaseId })
      throw error
    }
    if (!result.success) {
      // Read before step telemetry renames the request's error.
      let recoveryCause: string | undefined
      if (
        platform === 'china_android' &&
        product.product_type === 'subscription' &&
        result.error === 'interrupted'
      ) {
        const recovery = await recoverInterruptedSubscription(
          api.service,
          userId,
          currentSubscriptionExpiresAt,
        )
        if (recovery.status) return recovery.status
        if (recovery.lastRequestError !== undefined) {
          recoveryCause = failureCause(recovery.lastRequestError)
          onStepError?.(recovery.lastRequestError, {
            step: 'recover_interrupted_purchase',
            purchaseId,
          })
        }
      }
      // Once China Android recovery is exhausted, 'interrupted' follows the
      // existing cancelled-purchase path. Every other store error remains a
      // failed purchase. See ADR 0040 and its China Android exception, ADR 0008.
      markPurchase(result.error === 'interrupted' ? 'cancelled' : 'failed')
      const error = new NativeStoreError(result.error as NativeStoreErrorType)
      // A recovery check that could not be made leaves the payment uncertain,
      // which matters more to support than the interruption itself.
      if (recoveryCause === undefined) {
        tagFailure(error, 'native_purchase', { purchaseId })
      } else {
        tagFailure(error, 'recover_interrupted_purchase', {
          cause: recoveryCause,
          purchaseId,
        })
      }
      void logEvent?.({
        event_name: 'component_event',
        event_params: {
          type: 'dialog',
          app_action: 'show',
          component_name: `sub_paid_fail_${error.message.replace(/ /g, '_')}`,
        },
      })
      onStepError?.(error, { step: 'native_purchase', purchaseId })
      throw error
    }
    return result.data.transaction_id
  }

  /**
   * Closes every unclosed subscription transaction ("close all", ADR 0045):
   * the rejected one, and every subscription one the store lists, leaving
   * consumables alone (the same filter restore uses). Never throws: a failed
   * listing or close is reported, and the caller goes on.
   */
  const closeAllUnclosedSubscriptionTransactions = async (
    rejectedTransactionId: string,
  ) => {
    const transactionIds = new Set([rejectedTransactionId])
    try {
      const listing = await withTimeout(
        getAllUnclosedTransactions,
        'Listing unclosed transactions timed out',
      )
      if (!listing.success) {
        throw new FailureCauseError(
          'native_failed',
          'Listing unclosed transactions failed',
        )
      }
      for (const transaction of listing.data.transactions) {
        if (transaction.product_type === 'subscription') {
          transactionIds.add(transaction.transaction_id)
        }
      }
    } catch (error) {
      // A listing that cannot be read, or that native answers with
      // `success: false`, counts as empty: the rejected transaction is still
      // closed.
      onStepError?.(error, { step: 'list_unclosed_transactions', purchaseId })
    }
    // One close that fails must not keep the others, or the purchase, from
    // going on.
    for (const transaction_id of transactionIds) {
      try {
        await withTimeout(
          () => closeTransaction({ transaction_id }),
          'Closing an unclosed transaction timed out',
        )
      } catch (error) {
        onStepError?.(error, {
          step: 'close_transaction',
          purchaseId,
          transactionId: transaction_id,
        })
      }
    }
  }

  /**
   * Ends a purchase on a subscription conflict (409/412): closes every unclosed
   * subscription transaction and returns the error to throw, naming the
   * owning account. The purchase record is left as it is.
   */
  const closeAllOnSubscriptionConflict = async (
    error: unknown,
    rejectedTransactionId: string,
  ) => {
    const ownerUid = await readOwnerUid(error)
    await closeAllUnclosedSubscriptionTransactions(rejectedTransactionId)
    // Tag first: step telemetry renames the step's error, so it gets the
    // cause, never the error thrown.
    const conflictError = tagFailure(
      new SubscriptionConflictError(ownerUid, error),
      'post_transaction',
      { purchaseId, transactionId: rejectedTransactionId },
    )
    onStepError?.(error, {
      step: 'post_transaction',
      purchaseId,
      transactionId: rejectedTransactionId,
    })
    return conflictError
  }

  // 2. Call native app to let user pay
  const initialPurchase = await executeNativePurchase()
  if (typeof initialPurchase !== 'string') return initialPurchase
  let transactionId = initialPurchase

  // Debug: simulate AfterPaymentError for testing restore flow
  if (debugSimulateAfterPaymentError) {
    throw new AfterPaymentError(purchaseId, transactionId, 'debug_simulation')
  }

  // 3. Mark purchase as processing
  try {
    await api.service.post(
      `store/users/${userId}/purchase/${purchaseId}/mark-processing`,
    )
  } catch (error) {
    // Tag first: step telemetry renames the step's error.
    const afterPaymentError = tagFailure(
      new AfterPaymentError(
        purchaseId,
        transactionId,
        'mark_processing',
        error,
      ),
      'mark_processing',
      { purchaseId, transactionId },
    )
    onStepError?.(error, {
      step: 'mark_processing',
      purchaseId,
      transactionId,
    })
    throw afterPaymentError
  }

  // 4. Post transaction to receipt system server
  // A 410 means the transaction has expired: the store handed back a stale
  // transaction instead of taking a payment. Close every unclosed subscription
  // transaction, so the store has none left to hand back, and buy once more; a
  // second 410 means no payment was taken, so the purchase is cancelled
  // instead of left for Restore Purchases. A 409/412 is a subscription
  // conflict — another Forest account owns the subscription, never "already
  // posted" — which buying again cannot fix: close every unclosed subscription
  // transaction and stop. Any other failure may follow a real payment, so
  // nothing is closed and Restore Purchases can still link it (ADR 0045).
  try {
    await postTransaction(transactionId)
  } catch (error) {
    const rejection = classifyUploadRejection(error)
    if (rejection === null) throw postTransactionFailed(error, transactionId)
    if (rejection === 'subscription_conflict') {
      throw await closeAllOnSubscriptionConflict(error, transactionId)
    }
    await closeAllUnclosedSubscriptionTransactions(transactionId)
    // The first signature's nonce is spent (ADR 0036). Without a fresh one
    // the attempt ends having taken no payment: a cancelled purchase.
    try {
      promotionalOfferSignature = await signPromotionalOffer()
    } catch (signatureError) {
      markPurchase('cancelled')
      throw signatureError
    }
    const retryPurchase = await executeNativePurchase()
    if (typeof retryPurchase !== 'string') return retryPurchase
    transactionId = retryPurchase
    try {
      await postTransaction(transactionId)
    } catch (retryError) {
      switch (classifyUploadRejection(retryError)) {
        case 'stale_transaction':
          throw cancelStalePurchase(retryError, transactionId)
        case 'subscription_conflict':
          throw await closeAllOnSubscriptionConflict(retryError, transactionId)
        default:
          throw postTransactionFailed(retryError, transactionId)
      }
    }
  }

  // 5. Tell native app to close transaction
  try {
    await closeTransaction({ transaction_id: transactionId })
  } catch (error) {
    onStepError?.(error, {
      step: 'close_transaction',
      purchaseId,
      transactionId,
    })
  }

  // 6. Wait for service level to become subscription
  try {
    return fetchServiceLevelStatus
      ? await waitForServiceLevelFromSource({
          fetchStatus: fetchServiceLevelStatus,
          shouldResolve,
        })
      : await waitForSubscriptionStatus({
          serviceApi: api.service,
          userId,
          shouldResolve,
        })
  } catch (error) {
    onStepError?.(error, {
      step: 'poll_service_level',
      purchaseId,
      transactionId,
    })
    console.error('Failed to poll service level status', error)
    return null
  }
}

interface PromotionalOfferSignatureResponse {
  sku_id: string
  offer_identifier: string
  compact_jws: string
}

/** A promotional offer signature from the Infra API (ADR 0036). */
async function fetchPromotionalOfferSignature(
  infraApi: KyInstance,
  { userId, sku, offerId }: { userId: number; sku: string; offerId: string },
): Promise<string> {
  const response = await infraApi
    .post(`receipt/projects/6/users/${userId}/promotional_offers/signatures`, {
      json: { sku_id: sku, offer_identifier: offerId },
    })
    .json<PromotionalOfferSignatureResponse>()
  return response.compact_jws
}

/**
 * Runs a native call, rejecting with a `timeout` cause when it has not
 * settled within 5 s: the native bridge's own timeout is a year, and a
 * purchase must not wait on it. A synchronous throw rejects too.
 */
async function withTimeout<T>(
  run: () => Promise<T> | T,
  message: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      Promise.resolve().then(run),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new FailureCauseError('timeout', message)),
          NATIVE_TRANSACTION_CALL_TIMEOUT_MS,
        )
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

function classifyErrorCode(
  error: unknown,
): 'timeout' | 'network' | 'unknown' | (string & {}) {
  if (isHTTPError(error)) return String(error.response.status)
  if (isTimeoutError(error)) return 'timeout'
  if (isNetworkError(error as Error)) return 'network'
  return 'unknown'
}

/**
 * What the Receipt System means by rejecting an upload (ADR 0045): 410, the
 * transaction has expired (a stale transaction); 409/412, another Forest
 * account owns the subscription (a subscription conflict). `null` for any
 * other failure.
 */
function classifyUploadRejection(
  error: unknown,
): 'stale_transaction' | 'subscription_conflict' | null {
  if (!isHTTPError(error)) return null
  switch (error.response.status) {
    case 410:
      return 'stale_transaction'
    case 409:
    case 412:
      return 'subscription_conflict'
    default:
      return null
  }
}

/**
 * The owning account's uid from a 409/412 answer's `{ owner_uid }` (a
 * subscription conflict, ADR 0045), or `null` when the answer names none (a
 * 412 may not), cannot be read, or `error` is not an HTTP error.
 */
export async function readOwnerUid(error: unknown): Promise<number | null> {
  if (!isHTTPError(error)) return null
  try {
    // A clone: the answer stays readable for whoever reads it next.
    const body: unknown = await error.response.clone().json()
    const ownerUid =
      typeof body === 'object' && body !== null && 'owner_uid' in body
        ? body.owner_uid
        : null
    return typeof ownerUid === 'number' ? ownerUid : null
  } catch {
    return null
  }
}

interface PostPurchaseResponse {
  results: {
    purchase: { id: number }
  }[]
}

const SERVICE_LEVEL_POLL_INTERVAL_MS = 100
const SERVICE_LEVEL_SOURCE_POLL_GAP_MS = 1_000
export const SERVICE_LEVEL_POLL_TIMEOUT_MS = 15_000
const NATIVE_TRANSACTION_CALL_TIMEOUT_MS = 5_000
const INTERRUPTED_RECOVERY_DELAYS_MS = [0, 1_000, 2_000]
const INTERRUPTED_RECOVERY_REQUEST_TIMEOUT_MS = 10_000

export type ServiceLevelStatus =
  | {
      service_level: 'guest' | 'signup' | 'premium_signup' | 'premium_guest'
      is_premium: boolean
    }
  | SubscriptionServiceLevelStatus

export interface SubscriptionServiceLevelStatus {
  service_level: 'subscription'
  is_premium: boolean
  subscription: {
    auto_renew_sku_id: string
    cancel_reason: string
    cancelled_at: string
    created_at: string
    expires_at: string
    id: number
    is_auto_renewing: boolean
    is_in_introductory: boolean
    is_in_trial: boolean
    payment_system_psid: number
    promotional_offer_identifier: string
    receiptable_id: number
    sku_id: string
    starts_at: string
    transaction_id: string
    user_id: number
  }
}

/**
 * An injected service-level poll source (e.g. native `refreshServiceLevel`).
 * A `success: false` result or a rejection counts as one failed attempt.
 */
export type FetchServiceLevelStatus = () => Promise<Result<ServiceLevelStatus>>

export async function waitForSubscriptionStatus({
  serviceApi,
  userId,
  intervalMs = SERVICE_LEVEL_POLL_INTERVAL_MS,
  timeoutMs = SERVICE_LEVEL_POLL_TIMEOUT_MS,
  signal,
  shouldResolve = isSubscriptionStatus,
}: {
  serviceApi: KyInstance
  userId: number
  intervalMs?: number
  timeoutMs?: number
  signal?: AbortSignal
  shouldResolve?: (
    status: ServiceLevelStatus,
  ) => status is SubscriptionServiceLevelStatus
}): Promise<SubscriptionServiceLevelStatus> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  const combinedSignal = mergeAbortSignals(signal, controller.signal)

  try {
    return await pollServiceLevelStatus(
      serviceApi,
      userId,
      combinedSignal,
      intervalMs,
      shouldResolve,
    )
  } catch (error) {
    if (combinedSignal?.aborted) {
      throw new Error('Polling service level timed out')
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

async function pollServiceLevelStatus(
  serviceApi: KyInstance,
  userId: number,
  signal: AbortSignal | undefined,
  intervalMs: number,
  shouldResolve: (
    status: ServiceLevelStatus,
  ) => status is SubscriptionServiceLevelStatus,
): Promise<SubscriptionServiceLevelStatus> {
  while (true) {
    if (signal?.aborted) {
      throw new Error('Polling service level aborted')
    }
    const status = await serviceApi
      .get(`user/users/${userId}/service-levels/status`, { signal })
      .json<ServiceLevelStatus>()
    if (shouldResolve(status)) {
      return status
    }
    await sleep(intervalMs, signal)
  }
}

/**
 * Polls an injected source sequentially: each attempt starts 1 s after the
 * previous one returns. Failed attempts are skipped, and the deadline bounds
 * the whole poll — including an attempt that never returns.
 */
async function waitForServiceLevelFromSource({
  fetchStatus,
  shouldResolve = isSubscriptionStatus,
}: {
  fetchStatus: FetchServiceLevelStatus
  shouldResolve?: (
    status: ServiceLevelStatus,
  ) => status is SubscriptionServiceLevelStatus
}): Promise<SubscriptionServiceLevelStatus> {
  const controller = new AbortController()
  const timeoutId = setTimeout(
    () => controller.abort(),
    SERVICE_LEVEL_POLL_TIMEOUT_MS,
  )
  const deadline = new Promise<never>((_, reject) => {
    controller.signal.addEventListener(
      'abort',
      () => reject(new Error('Polling service level timed out')),
      { once: true },
    )
  })
  // Keep an unobserved rejection from surfacing once the poll has resolved.
  deadline.catch(() => {})

  const poll = async (): Promise<SubscriptionServiceLevelStatus> => {
    while (!controller.signal.aborted) {
      try {
        const result = await fetchStatus()
        if (result.success && shouldResolve(result.data)) return result.data
      } catch {
        // A failed attempt does not end the poll.
      }
      await sleep(SERVICE_LEVEL_SOURCE_POLL_GAP_MS, controller.signal)
    }
    throw new Error('Polling service level timed out')
  }

  try {
    return await Promise.race([poll(), deadline])
  } finally {
    clearTimeout(timeoutId)
    controller.abort()
  }
}

function isSubscriptionStatus(
  status: ServiceLevelStatus,
): status is SubscriptionServiceLevelStatus {
  return status.service_level === 'subscription'
}

async function recoverInterruptedSubscription(
  serviceApi: KyInstance,
  userId: number,
  currentSubscriptionExpiresAt: string | null | undefined,
): Promise<{
  status: SubscriptionServiceLevelStatus | null
  lastRequestError?: unknown
}> {
  let lastRequestError: unknown
  for (const delayMs of INTERRUPTED_RECOVERY_DELAYS_MS) {
    if (delayMs > 0) await sleep(delayMs)
    try {
      const status = await serviceApi
        .get(`user/users/${userId}/service-levels/status`, {
          retry: 0,
          timeout: INTERRUPTED_RECOVERY_REQUEST_TIMEOUT_MS,
        })
        .json<ServiceLevelStatus>()
      if (isRecoveredSubscriptionStatus(status, currentSubscriptionExpiresAt)) {
        return { status }
      }
    } catch (error) {
      lastRequestError = error
      // A failed request consumes its attempt; a later attempt may still
      // observe the server-side PingXX recovery.
    }
  }
  return { status: null, lastRequestError }
}

function isRecoveredSubscriptionStatus(
  status: ServiceLevelStatus,
  currentSubscriptionExpiresAt: string | null | undefined,
): status is SubscriptionServiceLevelStatus {
  if (!isSubscriptionStatus(status)) return false
  if (currentSubscriptionExpiresAt == null) return true

  const recoveredExpiry = Date.parse(status.subscription.expires_at)
  const currentExpiry = Date.parse(currentSubscriptionExpiresAt)
  return (
    Number.isFinite(recoveredExpiry) &&
    Number.isFinite(currentExpiry) &&
    recoveredExpiry > currentExpiry
  )
}

function sleep(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms)
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer)
          reject(new Error('Polling service level aborted'))
        },
        { once: true },
      )
    }
  })
}

function mergeAbortSignals(
  ...signals: (AbortSignal | undefined)[]
): AbortSignal | undefined {
  const activeSignals = signals.filter(Boolean) as AbortSignal[]
  if (activeSignals.length === 0) return undefined
  const controller = new AbortController()
  const onAbort = () => controller.abort()
  activeSignals.forEach((sig) => sig.addEventListener('abort', onAbort))
  if (activeSignals.some((sig) => sig.aborted)) {
    controller.abort()
  }
  return controller.signal
}
